import { Component, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
	SetupWizardCourseFormPayload,
	COURSE_CSV_LABELS,
	CourseCsvKeys,
	CourseCsvTemplate,
	COURSE_CSV_SPEC,
} from '@mira-avant/mtlibs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { TextField } from '../../../fields/setup-wizard/text-field/text-field';
import { CheckboxField } from '../../../fields/setup-wizard/checkbox-field/checkbox-field';
import { IntegerField } from '../../../fields/setup-wizard/integer-field/integer-field';
import { LanguageDropdownField } from '../../../fields/setup-wizard/language-dropdown-field/language-dropdown-field';
import { RubricCodeDropdownField } from '../../../fields/setup-wizard/rubric-code-dropdown-field/rubric-code-dropdown-field';
import { ProductNameDropdownField } from '../../../fields/setup-wizard/product-name-dropdown-field/product-name-dropdown-field';
import { TextareaField } from '../../../fields/setup-wizard/textarea-field/textarea-field';
import { FormUtil } from '../../../../utils/form.util';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { v4 } from 'uuid';

type CourseForm = {
	[CourseCsvKeys.ssoCourseId]: FormControl<string | null>;
	[CourseCsvKeys.defaultLangId]: FormControl<string | null>;
	[CourseCsvKeys.displayOrder]: FormControl<number | null>;
	[CourseCsvKeys.rubricCode]: FormControl<string | null>;
	[CourseCsvKeys.productName]: FormControl<string | null>;
	[CourseCsvKeys.enName]: FormControl<string | null>;
	[CourseCsvKeys.enDescriptionShort]: FormControl<string | null>;
	[CourseCsvKeys.enDescriptionHtml]: FormControl<string | null>;
	[CourseCsvKeys.esName]: FormControl<string | null>;
	[CourseCsvKeys.esDescriptionShort]: FormControl<string | null>;
	[CourseCsvKeys.esDescriptionHtml]: FormControl<string | null>;
	[CourseCsvKeys.courseUuid]: FormControl<string | null>;
	[CourseCsvKeys.clientUuid]: FormControl<string | null>;
	[CourseCsvKeys.active]: FormControl<boolean | null>;
};

@Component({
	selector: 'app-setup-wizard-course-form',
	imports: [
		ReactiveFormsModule,
		NzButtonModule,
		NzFormModule,
		NzInputModule,
		NzSpinModule,
		TextField,
		CheckboxField,
		IntegerField,
		LanguageDropdownField,
		RubricCodeDropdownField,
		ProductNameDropdownField,
		TextareaField,
	],
	standalone: true,
	templateUrl: './setup-wizard-course-form.html',
	styleUrl: './setup-wizard-course-form.scss',
})
export class SetupWizardCourseForm {
	public edit = input<CourseCsvTemplate | null>(null);
	public loading = input<boolean>(false);
	public onClear = output<void>();
	public onSubmit = output<SetupWizardCourseFormPayload>();
	public CourseCsvKeys = CourseCsvKeys;
	public COURSE_CSV_LABELS = COURSE_CSV_LABELS;
	public Validators = Validators;
	private clientUuid: string | undefined;
	private sub?: Subscription;

	public form = new FormGroup<CourseForm>({
		[CourseCsvKeys.ssoCourseId]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[CourseCsvKeys.defaultLangId]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[CourseCsvKeys.displayOrder]: new FormControl<number | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[CourseCsvKeys.rubricCode]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[CourseCsvKeys.productName]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[CourseCsvKeys.enName]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[CourseCsvKeys.enDescriptionShort]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[CourseCsvKeys.enDescriptionHtml]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[CourseCsvKeys.esName]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[CourseCsvKeys.esDescriptionShort]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[CourseCsvKeys.esDescriptionHtml]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[CourseCsvKeys.courseUuid]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[CourseCsvKeys.clientUuid]: new FormControl<string | null>(null, {
			nonNullable: false,
			validators: [],
		}),
		[CourseCsvKeys.active]: new FormControl<boolean | null>(null, {
			nonNullable: false,
			validators: [],
		}),
	});

	constructor(private route: ActivatedRoute) {
		FormUtil.applyCsvSpecValidators(this.form, COURSE_CSV_SPEC);

		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];

			this.form.patchValue({
				[CourseCsvKeys.clientUuid]: this.clientUuid,
			});
		});
	}

	ngOnChanges() {
		const edit = this.edit();

		if (edit) {
			// If edit contains a courseUuid we can remove all validators for ssoCourseId
			if (edit[CourseCsvKeys.courseUuid]) {
				this.form.get(CourseCsvKeys.ssoCourseId)?.clearValidators();
				this.form.get(CourseCsvKeys.ssoCourseId)?.updateValueAndValidity();
			}

			this.form.patchValue({
				[CourseCsvKeys.ssoCourseId]: edit[CourseCsvKeys.ssoCourseId],
				[CourseCsvKeys.defaultLangId]: edit[CourseCsvKeys.defaultLangId],
				[CourseCsvKeys.displayOrder]: edit[CourseCsvKeys.displayOrder],
				[CourseCsvKeys.rubricCode]: edit[CourseCsvKeys.rubricCode],
				[CourseCsvKeys.productName]: edit[CourseCsvKeys.productName],
				[CourseCsvKeys.enName]: edit[CourseCsvKeys.enName],
				[CourseCsvKeys.enDescriptionShort]: edit[CourseCsvKeys.enDescriptionShort],
				[CourseCsvKeys.enDescriptionHtml]: edit[CourseCsvKeys.enDescriptionHtml],
				[CourseCsvKeys.esName]: edit[CourseCsvKeys.esName],
				[CourseCsvKeys.esDescriptionShort]: edit[CourseCsvKeys.esDescriptionShort],
				[CourseCsvKeys.esDescriptionHtml]: edit[CourseCsvKeys.esDescriptionHtml],
				[CourseCsvKeys.courseUuid]: edit[CourseCsvKeys.courseUuid],
				[CourseCsvKeys.clientUuid]: edit[CourseCsvKeys.clientUuid],
				[CourseCsvKeys.active]: edit[CourseCsvKeys.active],
			});
		} else {
			this.form.patchValue({ [CourseCsvKeys.courseUuid]: v4() });
		}
	}

	onFormSubmit() {
		if (this.form.valid) {
			const value = this.form.getRawValue();

			this.onSubmit.emit({
				[CourseCsvKeys.ssoCourseId]: value[CourseCsvKeys.ssoCourseId] ?? undefined,
				[CourseCsvKeys.defaultLangId]: value[CourseCsvKeys.defaultLangId] ?? undefined,
				[CourseCsvKeys.displayOrder]: value[CourseCsvKeys.displayOrder] ?? undefined,
				[CourseCsvKeys.rubricCode]: value[CourseCsvKeys.rubricCode] ?? undefined,
				[CourseCsvKeys.productName]: value[CourseCsvKeys.productName] ?? undefined,
				[CourseCsvKeys.enName]: value[CourseCsvKeys.enName] ?? undefined,
				[CourseCsvKeys.enDescriptionShort]: value[CourseCsvKeys.enDescriptionShort] ?? undefined,
				[CourseCsvKeys.enDescriptionHtml]: value[CourseCsvKeys.enDescriptionHtml] ?? undefined,
				[CourseCsvKeys.esName]: value[CourseCsvKeys.esName] ?? undefined,
				[CourseCsvKeys.esDescriptionShort]: value[CourseCsvKeys.esDescriptionShort] ?? undefined,
				[CourseCsvKeys.esDescriptionHtml]: value[CourseCsvKeys.esDescriptionHtml] ?? undefined,
				[CourseCsvKeys.courseUuid]: value[CourseCsvKeys.courseUuid] ?? undefined,
				[CourseCsvKeys.clientUuid]: value[CourseCsvKeys.clientUuid] ?? undefined,
				[CourseCsvKeys.active]: value[CourseCsvKeys.active] ?? undefined,
			});

			this.form.reset();
		} else {
			this.form.markAllAsTouched();
		}
	}

	onClearClicked() {
		this.onClear.emit();
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
